import { useState, useEffect } from 'react';
import { Account, TaskDefinitionSummary, DocumentContent, TaskResult } from '../types';

const CACHE_KEY = 'inboxehr_last_selected_template';

export const useUploaderState = (initialAccount: Account) => {
  const [currentAccount, setCurrentAccount] = useState<Account>(initialAccount);
  const [templates, setTemplates] = useState<TaskDefinitionSummary[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [attachments, setAttachments] = useState<DocumentContent[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [finalResult, setFinalResult] = useState<TaskResult | null>(null);
  const [currentStage, setCurrentStage] = useState(1);
  const [isProcessingComplete, setIsProcessingComplete] = useState(false);
  const [isManageTemplatesModalVisible, setIsManageTemplatesModalVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showTemplateSettingsModal, setShowTemplateSettingsModal] = useState(false);

  // Load cached template on mount
  useEffect(() => {
    try {
      const cachedTemplate = localStorage.getItem(CACHE_KEY);
      if (cachedTemplate) {
        setSelectedTemplate(cachedTemplate);
      }
    } catch (error) {
      console.warn('Failed to load cached template:', error);
    }
  }, []);

  // Auto-select first template if no template is selected and templates are available
  useEffect(() => {
    if (!selectedTemplate && templates.length > 0) {
      const firstTemplate = templates[0];
      setSelectedTemplate(firstTemplate.id);
    }
  }, [templates]); // Remove selectedTemplate from dependency to avoid infinite loop
  
  // Ensure we always have a valid template selected when templates change
  useEffect(() => {
    if (templates.length > 0 && (!selectedTemplate || !templates.some(t => t.id === selectedTemplate))) {
      const firstTemplate = templates[0];
      setSelectedTemplate(firstTemplate.id);
    }
  }, [templates, selectedTemplate]);

  // Validate cached template against available templates
  useEffect(() => {
    if (selectedTemplate && templates.length > 0) {
      const templateExists = templates.some(template => template.id === selectedTemplate);
      if (!templateExists) {
        // Clear invalid cached template
        setSelectedTemplate('');
        try {
          localStorage.removeItem(CACHE_KEY);
        } catch (error) {
          console.warn('Failed to clear invalid cached template:', error);
        }
      }
    }
  }, [selectedTemplate, templates]);

  // Cache template when it changes
  useEffect(() => {
    if (selectedTemplate) {
      try {
        localStorage.setItem(CACHE_KEY, selectedTemplate);
      } catch (error) {
        console.warn('Failed to cache template:', error);
      }
    }
  }, [selectedTemplate]);

  return {
    // Account state
    currentAccount,
    setCurrentAccount,
    
    // Template state
    templates,
    setTemplates,
    selectedTemplate,
    setSelectedTemplate,
    
    // File state
    attachments,
    setAttachments,
    
    // Processing state
    isLoading,
    setIsLoading,
    error,
    setError,
    finalResult,
    setFinalResult,
    currentStage,
    setCurrentStage,
    isProcessingComplete,
    setIsProcessingComplete,
    
    // Modal state
    isManageTemplatesModalVisible,
    setIsManageTemplatesModalVisible,
    showSettings,
    setShowSettings,
    showTemplateSettingsModal,
    setShowTemplateSettingsModal,
    
  };
}; 